import { Close, Place, Search, Star } from "@mui/icons-material";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface GoogleMapsPlace {
  place_id?: string;
  placeId?: string;
  data_id?: string;
  data_cid?: string;
  title?: string;
  name?: string;
  address?: string;
  phone?: string;
  website?: string;
  rating?: number;
  reviews?: number;
  type?: string | string[];
  category?: string | string[];
  [key: string]: any;
}

interface GoogleMapsSearchDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  companyId: string;
  listId: string;
  onSuccess?: () => void;
  onBackgroundTaskCreated?: () => void;
}

const getPlaceKey = (p: GoogleMapsPlace): string =>
  p.place_id || p.placeId || p.data_id || p.data_cid || "";

const GoogleMapsSearchDialog: React.FC<GoogleMapsSearchDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  companyId,
  listId,
  onSuccess,
  onBackgroundTaskCreated,
}) => {
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [places, setPlaces] = useState<GoogleMapsPlace[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleClose = () => {
    if (searching || importing) return;
    setQuery("");
    setLocation("");
    setPlaces([]);
    setSelected(new Set());
    setError(null);
    setSearched(false);
    onClose();
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setError(null);
    setPlaces([]);
    setSelected(new Set());
    try {
      const token = await getToken();
      const resp = await axios.post(
        `${hostname}/lists/${listId}/google-maps/search`,
        {
          companyId,
          query: query.trim(),
          location: location.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const results: GoogleMapsPlace[] = resp.data?.places || [];
      setPlaces(results);
      setSelected(new Set(results.map(getPlaceKey).filter(Boolean)));
      setSearched(true);
    } catch (e: any) {
      setError(parseError(e));
    } finally {
      setSearching(false);
    }
  };

  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === places.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(places.map(getPlaceKey).filter(Boolean)));
    }
  };

  const handleImport = async () => {
    const toImport = places.filter((p) => selected.has(getPlaceKey(p)));
    if (toImport.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      const token = await getToken();
      const resp = await axios.post(
        `${hostname}/lists/${listId}/google-maps/import`,
        { companyId, places: toImport },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = resp.data || {};
      if (data.status === "background") {
        eventListener({
          type: "success",
          message: data.message || "Import started in background",
        });
        if (onBackgroundTaskCreated) onBackgroundTaskCreated();
      } else {
        const imported = data.imported || 0;
        const skipped = data.skipped || 0;
        eventListener({
          type: "success",
          message: `Imported ${imported} place${imported === 1 ? "" : "s"}${
            skipped ? `, skipped ${skipped} duplicate${skipped === 1 ? "" : "s"}` : ""
          }`,
        });
        if (onSuccess) onSuccess();
      }
      handleClose();
    } catch (e: any) {
      setError(parseError(e));
    } finally {
      setImporting(false);
    }
  };

  const renderCategory = (p: GoogleMapsPlace) => {
    const c = p.type || p.category;
    if (Array.isArray(c)) return c.join(", ");
    return c || "";
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Place />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Google Maps Search
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={searching || importing}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Search for businesses on Google Maps and import them into your list.
            Include a city or region in your query (e.g., "coffee shops in
            Austin"), or specify coordinates below.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              label="Search"
              placeholder='e.g., "dentists in Miami"'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim() && !searching) handleSearch();
              }}
              autoFocus
            />
            <TextField
              label="Location (optional)"
              placeholder="@lat,lng,zoom"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              sx={{ minWidth: { sm: 220 } }}
              helperText="e.g., @40.7455,-74.0083,15z"
            />
            <LoadingButton
              variant="contained"
              onClick={handleSearch}
              loading={searching}
              disabled={!query.trim()}
              startIcon={<Search />}
              sx={{ minWidth: 120, alignSelf: { sm: "flex-start" }, height: 56 }}
            >
              Search
            </LoadingButton>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {searched && !searching && places.length === 0 && !error && (
            <Alert severity="info">No places found. Try a different query.</Alert>
          )}

          {places.length > 0 && (
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {selected.size} of {places.length} selected
                </Typography>
              </Stack>
              <TableContainer
                sx={{
                  maxHeight: 480,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selected.size > 0 && selected.size < places.length
                          }
                          checked={
                            places.length > 0 && selected.size === places.length
                          }
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Website</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {places.map((p, idx) => {
                      const key = getPlaceKey(p) || `idx-${idx}`;
                      const isSelected = selected.has(getPlaceKey(p));
                      return (
                        <TableRow
                          key={key}
                          hover
                          selected={isSelected}
                          onClick={() => {
                            const k = getPlaceKey(p);
                            if (k) toggle(k);
                          }}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isSelected} />
                          </TableCell>
                          <TableCell>{p.title || p.name}</TableCell>
                          <TableCell>{p.address || ""}</TableCell>
                          <TableCell>{renderCategory(p)}</TableCell>
                          <TableCell>
                            {p.rating ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                              >
                                <Star fontSize="inherit" />
                                <span>
                                  {p.rating}
                                  {p.reviews ? ` (${p.reviews})` : ""}
                                </span>
                              </Stack>
                            ) : (
                              ""
                            )}
                          </TableCell>
                          <TableCell>{p.phone || ""}</TableCell>
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {p.website ? (
                              <Link
                                href={p.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                              >
                                {p.website}
                              </Link>
                            ) : (
                              ""
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        <LoadingButton onClick={handleClose} disabled={searching || importing}>
          Cancel
        </LoadingButton>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleImport}
          loading={importing}
          disabled={selected.size === 0 || searching}
        >
          Import {selected.size > 0 ? `(${selected.size})` : ""}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleMapsSearchDialog;
